import React from "react";

import style from "./styles.module.scss";

export type TableAlign = "start" | "center" | "end";

export interface TableColumn<T> {
  key: string;
  header: React.ReactNode;
  render(row: T): React.ReactNode;
  align?: TableAlign;
  width?: string;
  srOnlyHeader?: boolean;
}

export interface TableProps<T> {
  caption: string;
  columns: ReadonlyArray<TableColumn<T>>;
  rows: ReadonlyArray<T>;
  rowKey(row: T): string | number;
  loading?: boolean;
  loadingRows?: number;
  emptyState?: React.ReactNode;
  renderCompactRow?(row: T): React.ReactNode;
  className?: string;
}

const alignClasses: { [key in TableAlign]: string } = {
  start: style.alignStart,
  center: style.alignCenter,
  end: style.alignEnd,
};

export default function Table<T>({
  caption,
  columns,
  rows,
  rowKey,
  loading = false,
  loadingRows = 5,
  emptyState,
  renderCompactRow,
  className = "",
}: TableProps<T>) {
  const empty = !loading && rows.length === 0;
  const compact = Boolean(renderCompactRow);

  return (
    <div className={`${style.container} ${className}`.trim()} aria-busy={loading}>
      <table className={`${style.table} ${compact ? style.hideOnCompact : ""}`.trim()}>
        <caption className={style.visuallyHidden}>{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={`pgm-title-small ${style.headerCell} ${alignClasses[column.align || "start"]}`}
                style={column.width ? { width: column.width } : undefined}
              >
                <span className={column.srOnlyHeader ? style.visuallyHidden : undefined}>{column.header}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && skeletonRows(loadingRows).map((index) => (
            <tr key={`skeleton-${index}`} className={style.row}>
              {columns.map((column) => (
                <td key={column.key} className={style.cell}>
                  <span aria-hidden="true" className={style.skeleton} />
                </td>
              ))}
            </tr>
          ))}
          {!loading && rows.map((row) => (
            <tr key={rowKey(row)} className={style.row}>
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={`pgm-body-medium ${style.cell} ${alignClasses[column.align || "start"]}`}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {compact && renderCompactRow && (
        <ul className={style.compactList} aria-label={caption}>
          {loading && skeletonRows(loadingRows).map((index) => (
            <li key={`skeleton-${index}`} className={style.compactItem}>
              <span aria-hidden="true" className={style.skeleton} />
              <span aria-hidden="true" className={`${style.skeleton} ${style.skeletonShort}`} />
            </li>
          ))}
          {!loading && rows.map((row) => (
            <li key={rowKey(row)} className={style.compactItem}>
              {renderCompactRow(row)}
            </li>
          ))}
        </ul>
      )}

      {loading && <span className={style.visuallyHidden} role="status">{caption}</span>}
      {empty && emptyState && <div className={style.empty}>{emptyState}</div>}
    </div>
  );
}

function skeletonRows(count: number): number[] {
  return Array.from({ length: count }, (_, index) => index);
}
